import { type AuthApi, createAuthApi } from '@/platform/access/auth'
import { type ChildModeStore, createChildModeStore } from '@/platform/access/child-mode'
import {
  createLearnerSessionApi,
  type LearnerSessionApi,
} from '@/platform/access/learner-session'
import { type AppEnv, isLocalSupabaseUrl } from '@/platform/config/env'
import { createFamilyApi, type FamilyApi } from '@/platform/family/api'
import { type MaterialConsentApi } from '@/platform/family/consent'
import { type LearningService } from '@/platform/learning/service'
import { createLearnersApi, type LearnersApi } from '@/platform/learners/api'
import { type MaterialExtractionService } from '@/platform/materials/extraction'
import { type MaterialLearningApi } from '@/platform/materials/learning'
import { createReadAloudApi, type ReadAloudApi } from '@/platform/read-aloud/api'
import { type AppSupabaseClient } from '@/platform/supabase/client'

import { createDemoInsightLoader, createDemoMaterialsLoader } from './demo'
import { createLearningService } from './learning'
import {
  createMaterialConsent,
  createMaterialExtractionService,
  createMaterialLearning,
  materialBackendAllowed,
} from './materials'
import { type LoadSampleMaterials } from './screens/materials/model'
import { type LoadInsightPreview } from './screens/parent/insight'

/**
 * Everything the app shell talks to. Injected so screens and the onboarding
 * flow can be tested without a Supabase backend.
 */
export interface AppServices {
  readonly auth: AuthApi
  readonly family: FamilyApi
  readonly learners: LearnersApi
  /** The learner session kept on this device (D-16); not a credential check. */
  readonly childMode: ChildModeStore
  /** Learner PIN and learner session RPCs (D-16). */
  readonly learnerSessions: LearnerSessionApi
  /** Real RPCs on a local stack, or an explicitly chosen MOCK; none in pilot (D-02). */
  readonly learning: LearningService
  /** DEMO Learning Insight preview, local/development only; null otherwise (D-02). */
  readonly demoInsight: LoadInsightPreview | null
  /** DEMO school materials (Slice 2), local/development only; null otherwise (D-02). */
  readonly demoMaterials: LoadSampleMaterials | null
  /** Real school material reading (Slice 3): local stack or approved staging; disabled otherwise. */
  readonly materials: MaterialExtractionService
  /** School-material lessons and Parent Insight (D-17); same build rule as `materials`. */
  readonly materialLearning: MaterialLearningApi
  /** Parent consent to material processing (NFR-05); same build rule; null otherwise. */
  readonly materialConsent: MaterialConsentApi | null
  /** Read Aloud (KT1 integrated); same build rule as `materials`; null otherwise. */
  readonly readAloud: ReadAloudApi | null
}

export function createAppServices(client: AppSupabaseClient, env: AppEnv): AppServices {
  return {
    // Invite-only outside a local stack: a magic link never creates an account.
    auth: createAuthApi(client, window.location.origin, {
      allowSignUp: isLocalSupabaseUrl(env.VITE_SUPABASE_URL),
    }),
    family: createFamilyApi(client),
    learners: createLearnersApi(client),
    childMode: createChildModeStore(window.localStorage),
    learnerSessions: createLearnerSessionApi(client),
    learning: createLearningService(env, client),
    demoInsight: createDemoInsightLoader(env),
    demoMaterials: createDemoMaterialsLoader(env),
    materials: createMaterialExtractionService(env, client),
    materialLearning: createMaterialLearning(env, client),
    materialConsent: createMaterialConsent(env, client),
    readAloud: materialBackendAllowed(env) ? createReadAloudApi(client) : null,
  }
}
